import { createFileRoute, Link } from "@tanstack/react-router";
import {
  BookOpen,
  ListChecks,
  Sparkles,
  Quote,
  Type,
  SlidersHorizontal,
  Save,
  Trash2,
  Mic,
  Lightbulb,
  ChevronRight,
  Upload,
  FileText,
  FileSearch,
  Mic2,
  Repeat,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SiteHeader } from "@/components/SiteHeader";

export const Route = createFileRoute("/help")({
  head: () => ({
    meta: [
      { title: "How to use SmartEssay — Step-by-step Help Center" },
      { name: "description", content: "Step-by-step guide to SmartEssay: pick essay, summary, or key concepts, drop a title, and turn the framed outline into a finished assignment." },
      { property: "og:title", content: "How to use SmartEssay — Help Center" },
      { property: "og:description", content: "Step-by-step guide to getting the most out of SmartEssay's three modes, citations, tone, and vocabulary controls." },
      { property: "og:url", content: "https://smartessay.ai/help" },
      { name: "twitter:title", content: "SmartEssay Help Center" },
      { name: "twitter:description", content: "How to use SmartEssay's three modes, citations, and tone controls." },
    ],
    links: [{ rel: "canonical", href: "https://smartessay.ai/help" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is SmartEssay?",
            acceptedAnswer: { "@type": "Answer", text: "SmartEssay generates framed essay outlines, summaries, and key-concept maps with real word-for-word quotes and citations so students keep the thinking and the voice." },
          },
          {
            "@type": "Question",
            name: "What modes does SmartEssay offer?",
            acceptedAnswer: { "@type": "Answer", text: "Three modes: Essay (CER outline or full sample essay with cited evidence), Summary (structured APA 7 summary), and Key Concepts (themes and ideas with definitions)." },
          },
          {
            "@type": "Question",
            name: "Does SmartEssay support MLA and APA 7?",
            acceptedAnswer: { "@type": "Answer", text: "Yes. You can export polished PDFs in MLA or APA 7 with a student title page, double-spaced body, and a Works Cited or References page." },
          },
          {
            "@type": "Question",
            name: "Can I paste my own source text?",
            acceptedAnswer: { "@type": "Answer", text: "Yes. You can enter a title and let SmartEssay pull quotes from well-known works, or paste the article text directly for lesser-known sources." },
          },
        ],
      }),
    }],
  }),
  component: HelpPage,
});

function Step({ number, title, children }: { number: number; title: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
        {number}
      </div>
      <div className="flex-1">
        <h3 className="font-display text-lg font-semibold">{title}</h3>
        <div className="mt-2 text-sm text-muted-foreground leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof BookOpen;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
        <Icon className="h-5 w-5 text-primary" />
      </div>
      <h4 className="mt-3 font-display text-base font-semibold">{title}</h4>
      <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

function HelpPage() {
  return (
    <div className="min-h-screen bg-hero-gradient">
      <SiteHeader />

      <main className="mx-auto max-w-3xl px-6 pb-24">
        {/* Hero */}
        <div className="text-center">
          <p className="text-xs font-medium uppercase tracking-[0.22em] text-primary">The guide</p>
          <div className="mx-auto mt-3 h-px w-16 bg-gold/70" aria-hidden />
          <h1 className="mt-5 font-display text-4xl font-medium leading-tight tracking-tight sm:text-5xl">
            How to use <em>SmartEssay</em>
          </h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Everything you need to know to turn your assignments into structured, quote-backed outlines — fast.
          </p>
          <div className="mt-7">
            <Button asChild className="shadow-glow">
              <Link to="/generator">Start your first outline</Link>
            </Button>
          </div>
        </div>

        {/* Quick-start steps */}
        <section className="mt-14 space-y-8">
          <h2 className="font-display text-xl font-semibold">Quick start</h2>
          <Step number={1} title="Pick your assignment type">
            Choose from <strong>Essay</strong>, <strong>Summary</strong>, or <strong>Key Concepts</strong>. Each mode structures the output differently — essays get Claim-Evidence-Reasoning frames, summaries get main-idea scaffolds, and key concepts get thematic maps.
          </Step>
          <Step number={2} title="Enter the book or article title">
            Type the exact title of the work you're writing about. If it's a well-known book, SmartEssay can pull real quotes automatically. For lesser-known works, you can paste the text directly in the next step.
          </Step>
          <Step number={3} title="Add the source text (optional but recommended)">
            Toggle "Paste the text" and either <strong>drag and drop</strong> a file (.txt, .md, .pdf, .docx) or paste the passage, chapter, or article directly. This gives the most accurate quotes and citations. If you skip this, SmartEssay will use its knowledge of well-known works.
          </Step>
          <Step number={4} title="Paste your essay prompt or question">
            Copy the exact prompt from your teacher. This is what the AI uses to frame the outline around. You can use the microphone button to dictate it instead of typing.
            <span className="mt-2 block rounded-lg bg-muted px-3 py-2 text-xs">
              <Lightbulb className="mr-1.5 inline h-3.5 w-3.5 text-yellow-500" />
              Tip: The more detailed your prompt, the better the outline. Include any specific instructions your teacher gave.
            </span>
          </Step>
          <Step number={5} title="Choose your settings">
            Dial in <strong>evidences per claim</strong> (1–10, typed or picked), <strong>body paragraphs</strong> (any number you type — go past 5 for graduate or long-form work), a <strong>word target</strong> (pick a preset or type your own min/max), and <strong>writing level</strong> (intermediate or advanced).
          </Step>
          <Step number={6} title="Optional — match your own voice">
            Expand <strong>Match your writing voice</strong>, paste 80+ characters of your own writing (or save a permanent voice profile from <Link to="/account" className="text-primary underline-offset-2 hover:underline">Account</Link>), and SmartEssay will mirror your sentence rhythm, vocabulary tier, and connector choices — instead of the usual AI cadence. Anti-repetition guards run on every generation, even with voice off.
          </Step>
          <Step number={7} title="Click Generate">
            Hit the button and SmartEssay builds your outline in seconds. Each section includes real quotes with citations and sentence frames with blanks for your own analysis.
          </Step>
          <Step number={8} title="Export a polished draft PDF">
            Once your outline is ready, choose <strong>MLA</strong> or <strong>APA 7</strong> format and download a polished draft as a PDF. SmartEssay turns your scaffolded outline into a formatted paper with proper heading, double-spaced paragraphs, and a Works Cited or References page — edit & make it yours before turning it in.
          </Step>
        </section>

        {/* Peer-reviewed article summary callout */}
        <section className="mt-14 rounded-2xl border border-primary/30 bg-primary/5 p-6 shadow-soft">
          <div className="flex items-start gap-3">
            <ListChecks className="mt-1 h-5 w-5 text-primary" />
            <div>
              <h2 className="font-display text-xl font-semibold">Peer-reviewed article summaries (APA 7)</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                In <strong>Summary</strong> mode, check the <em>Peer-reviewed article</em> box. Paste the article into the source area and SmartEssay returns a structured APA 7 summary with these sections:
              </p>
              <ul className="mt-3 list-disc pl-5 text-sm text-muted-foreground space-y-1">
                <li>Introduction</li>
                <li>Subjects / Participants</li>
                <li>Overview of the Measurement Instrument</li>
                <li>Results</li>
                <li>Study Critique</li>
              </ul>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                One click <strong>copies it into Word or Google Docs</strong> with formatting intact, or download a full APA 7 PDF with student title page, double-spaced body, References page, and top-right page numbers.
              </p>
            </div>
          </div>
        </section>

        {/* Essay Reviewer callout */}
        <section id="reviewer" className="mt-14 rounded-2xl border border-primary/30 bg-primary/5 p-6 shadow-soft scroll-mt-20">
          <div className="flex items-start gap-3">
            <FileSearch className="mt-1 h-5 w-5 text-primary" />
            <div>
              <h2 className="font-display text-xl font-semibold">Essay Reviewer</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Already wrote a draft? Open the <strong>Essay Reviewer</strong> from the app header. Drop in your essay (paste or upload .txt/.md/.pdf/.docx), pick MLA or APA 7, and optionally upload the source material you're analyzing. You'll get a structured report with three parts:
              </p>
              <ul className="mt-3 list-disc pl-5 text-sm text-muted-foreground space-y-1">
                <li><strong>Evidence gaps</strong> — every place your claim needs support, with real word-for-word quotes from your source and copy-ready citations.</li>
                <li><strong>Logical flow map</strong> — paragraph-by-paragraph roles, transitions, and a strong/ok/weak score, plus specific flow issues between paragraphs.</li>
                <li><strong>Improvement ideas</strong> — concrete, prioritized fixes (high → low) covering thesis, evidence, reasoning, style, and counterarguments.</li>
              </ul>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                Source material is optional. Without it we'll still review flow and reasoning and tell you what kind of evidence is missing — we just won't invent quotes.
              </p>
              <div className="mt-4">
                <Button asChild size="sm" className="shadow-glow">
                  <Link to="/reviewer"><FileSearch className="mr-1.5 h-4 w-4" /> Open Essay Reviewer</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Feature cards */}
        <section className="mt-14">
          <h2 className="font-display text-xl font-semibold">Features explained</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon={BookOpen}
              title="Essay mode"
              description="Generates a structured CER outline with a claim, cited evidence from the text, and reasoning frames. Best for argumentative or analytical essays."
            />
            <FeatureCard
              icon={ListChecks}
              title="Summary mode"
              description="Boils the work down to its main ideas with a structured scaffold. Best when your teacher asks for a concise overview."
            />
            <FeatureCard
              icon={Sparkles}
              title="Key Concepts mode"
              description="Maps central themes and ideas from the text with definitions and connections. Best for thematic analysis assignments."
            />
            <FeatureCard
              icon={Quote}
              title="Real textual evidence"
              description="Every outline includes word-for-word passages from the source text with proper MLA or APA 7 citations. No made-up quotes."
            />
            <FeatureCard
              icon={ListChecks}
              title="Peer-reviewed article summaries"
              description="Inside Summary mode, get a structured APA 7 breakdown — Introduction, Participants, Measurement Instrument, Results, Critique — copy/paste-ready for Word or Docs, or as a PDF."
            />
            <FeatureCard
              icon={Type}
              title="Sentence frames"
              description="Scaffolds like 'This shows that ___ because ___' guide your thinking while leaving room for your own analysis."
            />
            <FeatureCard
              icon={SlidersHorizontal}
              title="Tone switcher"
              description="After generating, click Academic or Natural to rewrite the outline. Natural uses everyday language and shorter sentences."
            />
            <FeatureCard
              icon={Mic}
              title="Voice input"
              description="Tap the microphone icon next to any text field to dictate your prompt or title instead of typing. Great for long prompts."
            />
            <FeatureCard
              icon={Upload}
              title="Drag-and-drop file upload"
              description="Drop a .txt, .md, .pdf, or .docx file straight into the source text area. SmartEssay extracts the text automatically so you don't have to copy and paste."
            />
            <FeatureCard
              icon={FileText}
              title="PDF essay export"
              description="Turn any outline into a finished, formatted essay PDF in MLA or APA 7 style. Includes proper heading, double-spaced body paragraphs, and a complete Works Cited or References page."
            />
            <FeatureCard
              icon={Mic2}
              title="Voice matching"
              description="Paste a writing sample (or save a permanent profile) and SmartEssay mirrors your sentence length, vocabulary tier, and connector style so the draft sounds like you, not like AI."
            />
            <FeatureCard
              icon={Repeat}
              title="Anti-repetition guard"
              description="Every essay, summary, and reviewer report bans the most over-used AI words (delve, tapestry, moreover, navigate, in conclusion…) and enforces sentence variety so language feels fresh."
            />
            <FeatureCard
              icon={Save}
              title="Auto-save to history"
              description="Every outline you generate is saved automatically. Visit Past outlines to search, filter, and review anything you've made."
            />
          </div>
        </section>

        {/* Settings explained */}
        <section className="mt-14">
          <h2 className="font-display text-xl font-semibold">Settings explained</h2>
          <div className="mt-5 space-y-4">
            {[
              {
                label: "Evidences per claim",
                body: "Pick a preset (1–6) or type a custom value up to 10. More quotes give stronger support but a longer essay. Most teachers expect at least one quote per body paragraph.",
              },
              {
                label: "Body paragraphs",
                body: "Pick a preset or type your own number — there is no hard 5-paragraph cap. Use more for graduate-level, research, or long-form work; fewer for short responses.",
              },
              {
                label: "Target length",
                body: "Choose a preset (300–500, 500–800, 800–1200, 1200–1600) or type a custom min and max word count. SmartEssay calibrates section depth to hit your range.",
              },
              {
                label: "Writing level",
                body: "Intermediate uses clear, direct language. Advanced adds academic vocabulary and analytical phrasing like 'Furthermore' and 'This suggests that'.",
              },
              {
                label: "Peer-reviewed article (Summary mode)",
                body: "Turns Summary mode into a structured APA 7 breakdown of a research article. When on, paste the article text — the prompt, paragraph, word, and level fields are hidden because they're not needed.",
              },
              {
                label: "Match your writing voice",
                body: "Three options: Off (default style), Saved profile (managed in Account — analyzed once, reused everywhere), or Ad-hoc sample (paste a paragraph for this essay only). The profile captures sentence length, vocabulary tier, preferred connectors, and contractions, then steers the draft to match. Anti-repetition guards always run regardless.",
              },
            ].map((item) => (
              <div key={item.label} className="rounded-xl border border-border bg-card p-5 shadow-soft">
                <h4 className="font-display text-base font-semibold">{item.label}</h4>
                <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-14">
          <h2 className="font-display text-xl font-semibold">Frequently asked questions</h2>
          <Accordion type="multiple" className="mt-5">
            {[
              {
                q: "Do I have to paste the source text?",
                a: "No, but it helps. If you only enter the title, SmartEssay uses its knowledge of well-known books. For accuracy with passages, chapters, or articles, paste the text directly or drag and drop a .txt, .md, .pdf, or .docx file.",
              },
              {
                q: "Can I use SmartEssay for any book?",
                a: "Yes. For famous works, the AI knows the text well. For obscure or very recent works, pasting the text gives the best results.",
              },
              {
                q: "What does the tone switcher do?",
                a: "After generating an outline, you can click 'Natural' to rewrite it in a more conversational, student-like voice. Click 'Academic' to switch back to formal language.",
              },
              {
                q: "How do I find old outlines?",
                a: "Click Past outlines in the top navigation. You can search by title or prompt, filter by assignment type, and delete ones you don't need.",
              },
              {
                q: "How many free outlines do I get?",
                a: "New users get a limited number of free outlines. After that, you'll need a subscription to keep generating. You can still view and delete past outlines for free.",
              },
              {
                q: "Will my teacher know I used AI?",
                a: "SmartEssay gives you frames and quotes — you still fill in the blanks with your own thinking. The output is a starting scaffold, not a finished sample essay.",
              },
              {
                q: "Can I export my outline as a finished sample essay?",
                a: "Yes. After generating an outline, click Download PDF and choose MLA or APA 7 format. SmartEssay writes the full sample essay with proper heading, double-spaced paragraphs, and a Works Cited or References page.",
              },
              {
                q: "Does SmartEssay use the latest APA formatting?",
                a: "Yes — all APA exports follow APA 7 (current edition), including the student title page, References page, and in-text citation conventions.",
              },
              {
                q: "How do I summarize a peer-reviewed research article?",
                a: "Pick Summary mode, check 'Peer-reviewed article (APA 7 structured summary)', then paste the article. You'll get a five-section APA 7 summary you can copy straight into Word or Google Docs, or download as a PDF.",
              },
              {
                q: "Can I write more than 5 body paragraphs?",
                a: "Yes. The paragraph field accepts any number you type — go higher for graduate-level, research, or long-form writing. Word target is also fully customizable.",
              },
              {
                q: "Can I delete multiple outlines at once?",
                a: "Yes. On the Past outlines page, use the checkboxes to select multiple outlines, then click Delete selected.",
              },
              {
                q: "How does voice matching work?",
                a: "Paste 80+ characters of your own writing (a journal entry, a past essay, a long text message — anything that sounds like you). SmartEssay analyzes sentence length, vocabulary tier, contractions, and connector style, then steers the generation to match. Save it once in Account to reuse across every essay, or paste a fresh sample per essay.",
              },
              {
                q: "Why don't AI essays usually sound like me?",
                a: "Most AI writers lean on a small thesaurus and a few stock phrases (delve, tapestry, moreover, in conclusion, navigate the complexities). SmartEssay bans those words by default on every generation and enforces sentence-length variety, so even without voice matching the prose reads less robotic.",
              },
              {
                q: "Is my writing sample stored?",
                a: "Saved voice profiles store your samples and the extracted style profile under your account, protected by row-level security so only you can read them. Ad-hoc samples are sent with one request and not persisted. You can delete your saved profile any time from Account.",
              },
            ].map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                <AccordionTrigger className="text-left text-sm font-medium">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>

        {/* CTA */}
        <div className="mt-14 rounded-2xl border border-border bg-card p-8 text-center shadow-soft">
          <h3 className="font-display text-lg font-semibold">Ready to try it?</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick an assignment type, enter a title, and get your first outline in under a minute.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button asChild className="shadow-glow">
              <Link to="/generator">Create an outline <ChevronRight className="ml-1 h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/pricing">View pricing</Link>
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
